import React from 'react';
import Chart from 'react-apexcharts';
import {
  AccessTime,
  PeopleAltOutlined,
  Refresh,
  ThermostatOutlined,
  AirOutlined,
  FilterAltOutlined,
  ApartmentOutlined,
  InfoOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { Box, Button, Card, Grid, IconButton, Stack, Divider, Tooltip, useMediaQuery, Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import HomePageFilterBar from 'components/DevicesFilter/HomePageFilterBar.js';
import HomePageDialogSelect from 'components/Dialog/HomePageDialogSelect.js';
import GenericModal from 'components/GenericModal';
import MDBox from 'components/MDBox';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { getTodayDateFormatted } from 'helpers/helpers';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { brandColors } from 'types/colors';
import { TimeInMS } from 'types/time';
import BuildingDemandCard from './BuildingDemandCard.js';
import ImpactCard from './ImpactCard.js';
import HomePageCard from './HomePageCard.js';
// @mui material components
import { SvgIcon } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import {
  setFreshAirLoading,
  setHomePageDatesObj,
  setHvacLoading,
  setRealTimeLoading,
  fetchAreaSize,
  setAreaSizeLoading,
} from 'stores/sensor/homePageSlice';
import { ReactComponent as MoneySaveIcon } from '../../assets/images/icons/MoneySave.svg';
import { ReactComponent as VentilationLevelIcon } from 'assets/images/icons/Ventilation_Level.svg';
import { ReactComponent as CleanAirLevelIcon } from 'assets/images/icons/Clean_Air_Level.svg';
import { ReactComponent as ComfortLevelIcon } from 'assets/images/icons/Comfort_Level.svg';
import CustomSnackbar from '../../components/CustomSnackbar';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import EmptyStateMessage from '../../components/MessageBox/EmptyStateMessage';
import CleanAirEventsModal from './modals/CleanAirEventsModal';
import ComfortEventsModal from './modals/ComfortEventsModal';
import VentilationInfoModal from './modals/VentilationInfoModal';
import { alignItems, display, justifyContent, width } from '@mui/system';
const REFRESH_TIME_IN_MINUTES = 5;
const NONE_CALCULATED_AREA = 0.2;
const TimeRangeOptions = [
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'Dates range', value: 'Dates range' },
];

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  px: 2.5,
  py: 1,
  border: `1px solid ${brandColors.BLUE}`,
  color: brandColors.BLUE,
  backgroundColor: '#fff',
  boxShadow: '0 8px 18px rgba(20, 60, 160, 0.25)',
  '&:hover': {
    color: brandColors.BLUE_DARK,
    backgroundColor: brandColors.BLUE_DARK,
    boxShadow: '0 10px 22px rgba(20, 60, 160, 0.35)',
  },
};

const actionButtonIconSx = { scale: '1.5' };

const roundToOneDecimal = value => (Number.isFinite(value) ? Math.round(value * 10) / 10 : 0);
const formatOneDecimalTrim = value => {
  const rounded = roundToOneDecimal(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const reductionOptions = {
  chart: { type: 'donut' },
  labels: ['Reduction', 'Remaining'],
  colors: [brandColors.BLUE, brandColors.TURQUOISE_LIGHT],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
  plotOptions: {
    pie: {
      donut: {
        size: '82%',
      },
    },
  },
  tooltip: {
    enabled: true,
    fixed: {
      enabled: true,
      position: 'topCenter',
      offsetY: 0,
      offsetX: 0,
    },
    custom: function ({ series, seriesIndex }) {
      const value = Number.isFinite(series?.[seriesIndex]) ? series[seriesIndex] : 0;
      return `<div style="padding:6px 8px;font-weight:600;">${formatOneDecimalTrim(value)}%</div>`;
    },
  },
};

const baseVentilationOptions = {
  chart: { type: 'donut' },
  labels: ['Reduction', 'Remaining'],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
  plotOptions: {
    pie: {
      donut: {
        size: '82%',
      },
    },
  },
  tooltip: {
    enabled: true,
    fixed: {
      enabled: true,
      position: 'topCenter',
      offsetY: 0,
      offsetX: 0,
    },
    custom: function ({ series, seriesIndex }) {
      const value = Number.isFinite(series?.[seriesIndex]) ? series[seriesIndex] : 0;
      return `<div style="padding:6px 8px;font-weight:600;">${formatOneDecimalTrim(value)}%</div>`;
    },
  },
};

const getVentilationColor = value => {
  if (value > 84) return brandColors.GREEN;
  if (value > 70) return brandColors.YELLOW;
  return brandColors.RED;
};

const formatPercent = value => {
  const rounded = roundToOneDecimal(value);
  const display = formatOneDecimalTrim(value);
  return { rounded, display };
};

const getLongestDeviationText = streaks => {
  if (!Array.isArray(streaks) || streaks.length === 0) {
    return 'None';
  }

  const longestStreak = streaks.reduce((maxItem, item) => {
    const currentValue = Number(item?.rows_in_streak);
    const maxValue = Number(maxItem?.rows_in_streak);
    if (!Number.isFinite(currentValue)) return maxItem;
    if (!Number.isFinite(maxValue) || currentValue > maxValue) return item;
    return maxItem;
  }, null);

  const maxRowsInStreak = Number(longestStreak?.rows_in_streak);
  if (!Number.isFinite(maxRowsInStreak) || maxRowsInStreak === 0) {
    return 'None';
  }

  const value = Number(((maxRowsInStreak * 5) / 60).toFixed(1));
  const durationText = `${Number.isInteger(value) ? String(value) : value.toFixed(1)} h`;

  const reasonLabelByKey = {
    tvoc_avg: 'TVOC',
    mass_pm_2_5_avg: 'PM2.5',
  };
  const reasonLabel = reasonLabelByKey[longestStreak?.streak_reason];

  return reasonLabel ? `${durationText} (${reasonLabel})` : durationText;
};

const NewHomePage = () => {
  const [modalState, setModalState] = useState({ open: false, config: null });
  // ===== REDUX SELECTORS =====
  const dispatch = useDispatch();

  // Time range and user data
  const rangeObj = useSelector(state => state.homePage.homePageDatesObj);
  const impactData = useSelector(state => state.homePage.impactData);
  const impactLoading = useSelector(state => state.homePage.impactLoading);
  const [savedImpactData, setSavedImpactData] = useState(null);
  const isImpactDataReady = Boolean(savedImpactData);
  const impactOcpHours = {
    total_num_of_hours: Number(savedImpactData?.total_cav_hours ?? 0),
    total_num_of_ocp_hours: Number(savedImpactData?.total_occupied_cav_hours ?? 0),
  };

  const solaiPercentRaw = (impactData?.solai_record_count / impactData?.total_devices_record_count) * 100;
  const { rounded: solaiPercentRounded, display: solaiPercentDisplay } = formatPercent(solaiPercentRaw);

  const solaiOccupiedHoursPercentRaw = impactData?.solai_percentage_of_ocp_hrs;
  const { rounded: solaiOccupiedHoursPercentRounded, display: solaiOccupiedHoursPercentDisplay } =
    formatPercent(solaiOccupiedHoursPercentRaw);

  const ventilationAvgRaw = impactData?.ventilation_level_avg;
  const { rounded: ventilationAvgRounded, display: ventilationAvgDisplay } = formatPercent(ventilationAvgRaw);
  const ventilationAvgOccupiedRaw = impactData?.ventilation_level_avg_of_occupied;
  const { rounded: ventilationAvgOccupiedRounded, display: ventilationAvgOccupiedDisplay } =
    formatPercent(ventilationAvgOccupiedRaw);

  const cleanAirAvgRaw = impactData?.clean_air_level_avg;
  const { rounded: cleanAirAvgRounded, display: cleanAirAvgDisplay } = formatPercent(cleanAirAvgRaw);

  useEffect(() => {
    if (modalState.open) {
      document.body.classList.add('new-home-modal-open');
    } else {
      document.body.classList.remove('new-home-modal-open');
    }

    return () => {
      document.body.classList.remove('new-home-modal-open');
    };
  }, [modalState.open]);
  const cleanAirAvgOccupiedRaw = impactData?.clean_air_level_avg_of_occupied;
  const { rounded: cleanAirAvgOccupiedRounded, display: cleanAirAvgOccupiedDisplay } = formatPercent(cleanAirAvgOccupiedRaw);

  const comfortAvgRaw = impactData?.comfort_level_avg;
  const { rounded: comfortAvgRounded, display: comfortAvgDisplay } = formatPercent(comfortAvgRaw);
  const comfortAvgOccupiedRaw = impactData?.comfort_level_avg_of_occupied;
  const { rounded: comfortAvgOccupiedRounded, display: comfortAvgOccupiedDisplay } = formatPercent(comfortAvgOccupiedRaw);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [performancePieView, setPerformancePieView] = useState({
    ventilation: 'occupied',
    cleanAir: 'occupied',
    comfort: 'occupied',
  });
  const [adaptiveView, setAdaptiveView] = useState('cav');
  const [activePerformanceCard, setActivePerformanceCard] = useState('ventilation');
  const handleOpenModal = key => {
    setModalState({ open: true, config: modalConfigs[key] || null });
  };
  const handleCloseModal = () => {
    setModalState(s => ({ ...s, open: false }));
  };

  // Filter state - Redux managed
  const filterState = useSelector(state => state.persistence?.selectedFilterObject);
  const { distributor, customer, building, floor } = filterState || {
    distributor: 'all',
    customer: 'all',
    building: 'all',
    floor: 'all',
  };
  const modalConfigs = {
    ventilation: {
      title: 'Ventilation Evidence',
      titleIcon: (
        <VentilationLevelIcon
          style={{
            width: 32,
            height: 32,
            marginLeft: 8,
            verticalAlign: 'middle',
            color: '#344767',
            fill: '#344767',
          }}
        />
      ),
      Component: VentilationInfoModal,
      componentProps: {
        buildingId: building,
        floorNumber: floor,
        timeRange: rangeObj,
        onClose: handleCloseModal,
      },
      paperSx: { width: 'min(1628px, 95vw)', maxHeight: '80vh' },
    },
    cleanAir: {
      title: 'Air Events',
      titleIcon: (
        <CleanAirLevelIcon
          style={{
            width: 32,
            height: 32,
            marginLeft: 8,
            verticalAlign: 'middle',
            color: '#344767',
            fill: '#344767',
          }}
        />
      ),
      Component: CleanAirEventsModal,
      componentProps: {
        buildingId: building,
        floorNumber: floor,
        timeRange: rangeObj,
        onClose: handleCloseModal,
      },
      paperSx: { width: 'min(1628px, 95vw)', maxHeight: '80vh' },
    },
    comfort: {
      title: 'Thermal Events',
      titleIcon: (
        <ComfortLevelIcon
          style={{
            width: 32,
            height: 32,
            marginLeft: 8,
            verticalAlign: 'middle',
            color: '#344767',
            fill: '#344767',
          }}
        />
      ),
      Component: ComfortEventsModal,
      componentProps: {
        buildingId: building,
        floorNumber: floor,
        timeRange: rangeObj,
        onClose: handleCloseModal,
      },
      paperSx: { width: 'min(1628px, 95vw)', maxHeight: '80vh' },
    },
  };
  // ===== CORE REFRESH FUNCTION =====
  const isRefreshing = useRef(false);

  const refreshContent = useCallback([rangeObj, dispatch, building, floor]);

  // // ===== FILTER CHANGE HANDLER =====
  // const handleManualRefresh = useCallback(() => {
  //   hasAreaSizeFetch.current = false;
  //   refreshContent(true); // with area-size fetch
  // }, [refreshContent]);

  // ===== MINIMAL USEEFFECTS =====

  // 1. INITIAL DATA FETCH ON COMPONENT MOUNT
  useEffect(() => {
    if (building !== 'all') {
      // refreshContent(true); // with area-size
    }
  }, []);

  // 2. TIME RANGE CHANGES - Separate from filter changes
  useEffect(() => {
    if (rangeObj && building !== 'all') {
      // refreshContent();
    }
  }, [rangeObj, refreshContent]);

  // 3. AUTO-REFRESH INTERVAL - Simple timer
  useEffect(() => {
    // Only auto-refresh if we have a building selected
    if (building === 'all') return;
    // if building is selected
    const interval = setInterval(() => {
      // refreshContent(false);
    }, REFRESH_TIME_IN_MINUTES * 60000);

    return () => {
      clearInterval(interval);
    };
  }, [building, refreshContent]);

  useEffect(() => {
    if (!impactLoading && impactData) {
      setSavedImpactData(impactData);
    }
  }, [impactLoading, impactData]);

  const solaiPercentValue = Number.isFinite(solaiPercentRounded) ? solaiPercentRounded : 0;

  const solaiSeries = [solaiPercentValue, Math.max(0, roundToOneDecimal(100 - solaiPercentValue))];
  const solaiOccupiedHoursPercentValue = Number.isFinite(solaiOccupiedHoursPercentRounded) ? solaiOccupiedHoursPercentRounded : 0;
  const solaiOccupiedHoursSeries = [
    solaiOccupiedHoursPercentValue,
    Math.max(0, roundToOneDecimal(100 - solaiOccupiedHoursPercentValue)),
  ];
  const ventilationAvgOccupiedValue = Number.isFinite(ventilationAvgOccupiedRounded) ? ventilationAvgOccupiedRounded : 0;
  const ventilationAvgValue = Number.isFinite(ventilationAvgRounded) ? ventilationAvgRounded : 0;
  const cleanAirAvgOccupiedValue = Number.isFinite(cleanAirAvgOccupiedRounded) ? cleanAirAvgOccupiedRounded : 0;
  const cleanAirAvgValue = Number.isFinite(cleanAirAvgRounded) ? cleanAirAvgRounded : 0;
  const comfortAvgOccupiedValue = Number.isFinite(comfortAvgOccupiedRounded) ? comfortAvgOccupiedRounded : 0;
  const comfortAvgValue = Number.isFinite(comfortAvgRounded) ? comfortAvgRounded : 0;

  const ventilationSeries = [ventilationAvgOccupiedValue, Math.max(0, roundToOneDecimal(100 - ventilationAvgOccupiedValue))];
  const ventilationCavSeries = [ventilationAvgValue, Math.max(0, roundToOneDecimal(100 - ventilationAvgValue))];
  const cleanAirSeries = [cleanAirAvgOccupiedValue, Math.max(0, roundToOneDecimal(100 - cleanAirAvgOccupiedValue))];
  const cleanAirCavSeries = [cleanAirAvgValue, Math.max(0, roundToOneDecimal(100 - cleanAirAvgValue))];
  const comfortSeries = [comfortAvgOccupiedValue, Math.max(0, roundToOneDecimal(100 - comfortAvgOccupiedValue))];
  const comfortCavSeries = [comfortAvgValue, Math.max(0, roundToOneDecimal(100 - comfortAvgValue))];

  const ventilationOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(ventilationSeries[0]), brandColors.GREY],
  };
  const cleanAirOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(cleanAirSeries[0]), brandColors.GREY],
  };

  const ventilationCavOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(ventilationCavSeries[0]), brandColors.GREY],
  };

  const cleanAirCavOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(cleanAirCavSeries[0]), brandColors.GREY],
  };

  const comfortOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(comfortSeries[0]), brandColors.GREY],
  };

  const comfortCavOptions = {
    ...baseVentilationOptions,
    colors: [getVentilationColor(comfortCavSeries[0]), brandColors.GREY],
  };
  const isBuildingSelected = Boolean(building && building !== 'all' && building !== 'undefined');
  const selectBuildingInfoContent = (
    <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 4 }}>
      <Box
        sx={{
          height: 64,
          width: 64,
          borderRadius: '50%',
          backgroundColor: brandColors.GREY_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <ApartmentOutlined sx={{ fontSize: 34, color: brandColors.BLUE }} />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828' }}>
        Select a building to view impact data
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: brandColors.GREY }}>
        <InfoOutlined sx={{ fontSize: 18 }} />
        <Typography variant="body2" sx={{ color: brandColors.GREY }}>
          Use the filters above to choose a building and time range.
        </Typography>
      </Stack>
    </Stack>
  );

  const handlePerformancePieToggle = cardKey => {
    setPerformancePieView(prev => ({
      ...prev,
      [cardKey]: prev[cardKey] === 'occupied' ? 'cav' : 'occupied',
    }));
  };

  const renderSlidingPerformancePie = ({
    cardKey,
    occupiedDisplay,
    cavDisplay,
    occupiedOptions,
    cavOptions,
    occupiedSeries,
    cavSeries,
  }) => {
    const currentView = performancePieView[cardKey] === 'cav' ? 'cav' : 'occupied';
    const isOccupiedView = currentView === 'occupied';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: 240, pt: 0.5 }}>
          <Box sx={{ position: 'absolute', top: -6, right: 0, zIndex: 2, display: 'flex' }}>
            <IconButton
              size="small"
              onClick={() => handlePerformancePieToggle(cardKey)}
              disabled={isOccupiedView}
              sx={{ p: 0.1 }}>
              <KeyboardArrowLeft sx={{ scale: 1.5 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handlePerformancePieToggle(cardKey)}
              disabled={!isOccupiedView}
              sx={{ p: 0.1 }}>
              <KeyboardArrowRight sx={{ scale: 1.5 }} />
            </IconButton>
          </Box>
          <Box sx={{ width: 240, height: 244, overflow: 'hidden', mx: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                width: '200%',
                alignItems: 'flex-start',
                transform: isOccupiedView ? 'translateX(0%)' : 'translateX(-50%)',
                transition: 'transform 0.35s ease',
              }}>
              <Box
                sx={{
                  width: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}>
                <Box sx={{ position: 'relative', mx: 'auto' }}>
                  <Chart type="donut" options={occupiedOptions} series={occupiedSeries} width={160} height={160} />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                    <Stack spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000', lineHeight: 1, fontSize: '2rem' }}>
                        {occupiedDisplay}%
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 400,
                          color: '#000',
                          fontSize: '.8rem',
                          textAlign: 'center',
                        }}>
                        from occupied
                        <br />
                        hours
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PeopleAltOutlined sx={{ fontSize: 20, color: brandColors.BLUE }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1d1d1d' }}>
                    Occupied Hours
                  </Typography>
                </Stack>
                <Stack spacing={1} alignItems="center">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      background: brandColors.BLUE_LIGHT,
                      color: brandColors.BLUE,
                      borderRadius: '999px',
                      padding: '6px 10px',
                    }}>
                    <AccessTime sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
                      {impactOcpHours.total_num_of_ocp_hours}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.GREY_DARK }}>
                      hrs
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}>
                <Box sx={{ position: 'relative', mx: 'auto' }}>
                  <Chart type="donut" options={cavOptions} series={cavSeries} width={160} height={160} />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                    <Stack spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000', lineHeight: 1, fontSize: '2rem' }}>
                        {cavDisplay}%
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 400, color: '#000', fontSize: '.8rem' }}>
                        from CAV hours
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ApartmentIcon sx={{ fontSize: 20, color: brandColors.BLUE }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1d1d1d' }}>
                    CAV Hours (Baseline)
                  </Typography>
                </Stack>
                <Stack spacing={1} alignItems="center">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      background: brandColors.BLUE_LIGHT,
                      color: brandColors.BLUE,
                      borderRadius: '999px',
                      padding: '6px 10px',
                    }}>
                    <AccessTime sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
                      {impactOcpHours.total_num_of_hours}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.GREY_DARK }}>
                      hrs
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderAdaptiveSlider = () => {
    const isCavView = adaptiveView === 'cav';

    return (
      <Box sx={{ position: 'relative', width: 260 }}>
        <Box sx={{ position: 'absolute', top: -4, right: 0, zIndex: 2, display: 'flex' }}>
          <IconButton size="small" onClick={() => setAdaptiveView('cav')} disabled={isCavView} sx={{ p: 0.1 }}>
            <KeyboardArrowLeft sx={{ scale: 1.5 }} />
          </IconButton>
          <IconButton size="small" onClick={() => setAdaptiveView('occupied')} disabled={!isCavView} sx={{ p: 0.1 }}>
            <KeyboardArrowRight sx={{ scale: 1.5 }} />
          </IconButton>
        </Box>
        <Box sx={{ width: 260, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              width: '200%',
              transform: isCavView ? 'translateX(0%)' : 'translateX(-50%)',
              transition: 'transform 0.35s ease',
            }}>
            <Box
              sx={{
                width: '50%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography sx={{ fontSize: '1rem' }}>DCV-SOLAI Active </Typography>
              <Box sx={{ position: 'relative', mx: 'auto' }}>
                <Chart type="donut" options={reductionOptions} series={solaiSeries} width={160} height={160} />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                  <Stack spacing={0.5} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000', lineHeight: 1, fontSize: '2rem' }}>
                      {solaiPercentDisplay}%
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 400, color: '#000', fontSize: '.8rem' }}>
                      from CAV hours
                    </Typography>
                  </Stack>
                </Box>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ApartmentIcon sx={{ fontSize: 20, color: brandColors.BLUE }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1d1d1d' }}>
                  CAV Hours (Baseline)
                </Typography>
              </Stack>
              <Stack spacing={1} alignItems="center">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    background: brandColors.BLUE_LIGHT,
                    color: brandColors.BLUE,
                    borderRadius: '999px',
                    padding: '6px 10px',
                  }}>
                  <AccessTime sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {impactOcpHours.total_num_of_hours}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.GREY_DARK }}>
                    hrs
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box
              sx={{
                width: '50%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography sx={{ fontSize: '1rem', color: '#000' }}>DCV-SOLAI Active </Typography>
              <Box sx={{ position: 'relative', width: 160, height: 160, mx: 'auto' }}>
                <Chart type="donut" options={reductionOptions} series={solaiOccupiedHoursSeries} width={160} height={160} />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                  <Stack spacing={0.5} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000', lineHeight: 1, fontSize: '2rem' }}>
                      {solaiOccupiedHoursPercentDisplay}%
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 400,
                        color: '#000',
                        fontSize: '.8rem',
                        textAlign: 'center',
                      }}>
                      from occupied
                      <br />
                      hours
                    </Typography>
                  </Stack>
                </Box>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PeopleAltOutlined sx={{ fontSize: 20, color: brandColors.BLUE }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1d1d1d' }}>
                  Occupied Hours
                </Typography>
              </Stack>
              <Stack spacing={1} alignItems="center">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    background: brandColors.BLUE_LIGHT,
                    color: brandColors.BLUE,
                    borderRadius: '999px',
                    padding: '6px 10px',
                  }}>
                  <AccessTime sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {impactOcpHours.total_num_of_ocp_hours}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.GREY_DARK }}>
                    hrs
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const performanceCards = {
    ventilation: {
      title: 'Ventilation Performance',
      switchLabel: 'Ventilation',
      subheader: '62.1 - aligned outcome verification',
      icon: <VentilationLevelIcon />,
      occupiedDisplay: ventilationAvgOccupiedDisplay,
      cavDisplay: ventilationAvgDisplay,
      occupiedOptions: ventilationOptions,
      cavOptions: ventilationCavOptions,
      occupiedSeries: ventilationSeries,
      cavSeries: ventilationCavSeries,
      eventCount: savedImpactData?.number_of_co2_events,
      longestDeviation: savedImpactData?.ventilation_event_evidence_streak_rows_per_sensor,
      actionIcon: <AirOutlined sx={actionButtonIconSx} />,
      actionLabel: 'View Evidence',
      modalKey: 'ventilation',
      skeletonWidths: [180, 210],
    },
    cleanAir: {
      title: 'Clean Air Performance',
      switchLabel: 'Clean Air',
      subheader: 'PM2.5 + TVOC verification',
      icon: <CleanAirLevelIcon />,
      occupiedDisplay: cleanAirAvgOccupiedDisplay,
      cavDisplay: cleanAirAvgDisplay,
      occupiedOptions: cleanAirOptions,
      cavOptions: cleanAirCavOptions,
      occupiedSeries: cleanAirSeries,
      cavSeries: cleanAirCavSeries,
      eventCount: savedImpactData?.number_of_clean_air_events,
      longestDeviation: savedImpactData?.clean_air_event_evidence_streak_rows_per_sensor,
      actionIcon: <FilterAltOutlined sx={actionButtonIconSx} />,
      actionLabel: 'View Air Events',
      modalKey: 'cleanAir',
      skeletonWidths: [180, 210],
    },
    comfort: {
      title: 'Comfort Performance',
      switchLabel: 'Comfort',
      subheader: '55 - aligned comfort envelope',
      icon: <ComfortLevelIcon />,
      occupiedDisplay: comfortAvgOccupiedDisplay,
      cavDisplay: comfortAvgDisplay,
      occupiedOptions: comfortOptions,
      cavOptions: comfortCavOptions,
      occupiedSeries: comfortSeries,
      cavSeries: comfortCavSeries,
      eventCount: savedImpactData?.number_of_comfort_events,
      longestDeviation: savedImpactData?.comfort_event_evidence_streak_rows_per_sensor,
      actionIcon: <ThermostatOutlined sx={actionButtonIconSx} />,
      actionLabel: 'View Thermal Events',
      modalKey: 'comfort',
      skeletonWidths: [210],
    },
  };
  const activePerformance = performanceCards[activePerformanceCard];

  return (
    <DashboardLayout>
      <Box
        sx={{
          height: { lg: 'calc(100dvh - 25px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          pb: { lg: 1 },
          boxSizing: 'border-box',
          // Preserve the viewport-filling grid at normal and 110% zoom, including
          // on short displays. Only allow the cards to grow on extremely small
          // viewports, where their content can no longer fit safely in a row.
          '@media (max-height: 580px)': {
            height: 'auto',
          },
        }}>
        <CustomSnackbar
          {...snack}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          duration={3000}
          vertical="top"
          horizontal="left"
        />
        <DashboardNavbar objectName="Home" />
        <Typography
          fontSize="11px"
          sx={{
            paddingLeft: { xs: 0, sm: '12px' },
            marginTop: { xs: '-15px', sm: '-5px' },
            textAlign: { xs: 'center', sm: 'left' },
            flexShrink: 0,
          }}>
          Smart indoor air monitoring & management platform
        </Typography>
        <GenericModal open={modalState.open} onClose={handleCloseModal} {...(modalState.config || {})} />
        <MDBox pt={{ xs: 1 }} pb={1} sx={{ flexShrink: 0 }}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <MDBox
                mb={1}
                variant="gradient"
                borderRadius="lg"
                coloredShadow="light"
                id="topBar"
                sx={{
                  position: 'relative',
                  zIndex: 1200,
                }}
                style={{ background: '#DEE4F9' }}>
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'grid' },
                    gridTemplateColumns: { sm: '1fr auto 1fr' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    gap: { xs: 1, sm: 0 },
                    padding: '0px 16px',
                  }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="end"
                    className="top-bar"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: {
                        xs: 'fit-content',
                      },
                    }}>
                    <Typography variant="h4" color="dark">
                      Home
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: { xs: '100%', sm: 'auto' },
                    }}>
                    <DevicesListFilter
                      area={false}
                      status={false}
                      search={false}
                      hvacEquipment
                      showRefreshButton={false}
                      onRefreshClick={refreshContent}
                    />
                  </Box>

                  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                    <HomePageDialogSelect
                      buttonTitle="Time Range"
                      buttonSubTitle={`${rangeObj?.label || 'Today'}`}
                      dialogTitle="Choose Time Range"
                      color="black"
                      selectLabel="Time Range"
                      selectOptions={TimeRangeOptions}
                      onSelectionOK={setHomePageDatesObj}
                      default={rangeObj}
                      // onClose={() => refreshContent()}
                      toolTip=""
                    />
                  </Stack>
                </Box>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <Box
          className="home-page-body"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' },
            gridAutoRows: { lg: 'minmax(0, 1fr)' },
            gap: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            flex: { lg: 1 },
            minHeight: { lg: 0 },
            overflow: 'visible',
            '& .MuiCardHeader-root': {
              boxSizing: 'border-box',
              height: { xs: 115, sm: 90, md: 68 },
              minHeight: { xs: 115, sm: 90, md: 68 },
              maxHeight: { xs: 115, sm: 90, md: 68 },
            },
            '@media (min-width: 1200px) and (max-height: 900px)': {
              '& .MuiCardHeader-root': {
                height: 70,
                minHeight: 70,
                maxHeight: 70,
              },
            },
            '& > *': {
              minWidth: 0,
              maxWidth: '100%',
              minHeight: 0,
            },
            '& > .MuiCard-root, & > * > .MuiCard-root': {
              minHeight: 0,
              maxHeight: '100%',
            },
            '& .MuiCardContent-root': {
              padding: 0,
            },
            '& .MuiCardContent-root:last-child': {
              paddingBottom: 0,
            },
            '@media (max-height: 580px)': {
              flex: 'none',
              minHeight: 'auto',
              gridAutoRows: 'auto',
            },
          }}>
          <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 2' }, height: { lg: '100%' }, minHeight: { lg: 0 } }}>
            <ImpactCard rangeObj={rangeObj} building={building} floor={floor} />
          </Box>

          <HomePageCard
            title="Adaptive Operation"
            subheader=""
            content={isBuildingSelected ? renderAdaptiveSlider() : selectBuildingInfoContent}
          />

          <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 2' }, height: { lg: '100%' }, minHeight: { lg: 0 } }}>
            <BuildingDemandCard />
          </Box>

          <HomePageCard
            title={activePerformance.title}
            subheader={activePerformance.subheader}
            icon={activePerformance.icon}
            headerAction={
              <Box sx={{ display: 'flex', gap: 0.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                {Object.entries(performanceCards)
                  .filter(([cardKey]) => cardKey !== activePerformanceCard)
                  .map(([cardKey, performanceCard]) => (
                    <Button
                      key={cardKey}
                      size="small"
                      onClick={() => setActivePerformanceCard(cardKey)}
                      sx={{
                        minWidth: 0,
                        px: { xs: 0.5, sm: 0.75 },
                        py: 0.4,
                        border: '1px solid #fff',
                        borderRadius: 2,
                        color: '#fff',
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#fff',
                          backgroundColor: '#fff',
                        },
                      }}>
                      {performanceCard.switchLabel}
                    </Button>
                  ))}
              </Box>
            }
            content={
              <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
                {isBuildingSelected ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      alignItems: 'center',
                      justifyContent: 'start',
                      width: 'fit-content',
                    }}>
                    {renderSlidingPerformancePie({
                      cardKey: activePerformanceCard,
                      occupiedDisplay: activePerformance.occupiedDisplay,
                      cavDisplay: activePerformance.cavDisplay,
                      occupiedOptions: activePerformance.occupiedOptions,
                      cavOptions: activePerformance.cavOptions,
                      occupiedSeries: activePerformance.occupiedSeries,
                      cavSeries: activePerformance.cavSeries,
                    })}
                    <Box className="indexes-numbers" sx={{ width: 'fit-content', marginLeft: '1rem' }}>
                      {!isImpactDataReady ? (
                        <Stack spacing={1}>
                          {activePerformance.skeletonWidths.map(width => (
                            <Skeleton key={width} variant="rounded" width={width} height={24} animation="wave" />
                          ))}
                        </Stack>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '.5rem',
                            }}>
                            <Typography sx={{ fontSize: '1rem' }}>Events:</Typography>
                            <Typography sx={{ fontSize: '1.2rem', color: '#000', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {activePerformance.eventCount}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '.5rem',
                            }}>
                            <Typography sx={{ fontSize: '1rem' }}>Longest Deviation:</Typography>
                            <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {getLongestDeviationText(activePerformance.longestDeviation)}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Divider sx={{ borderColor: '#000', opacity: 0.8 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                          className="open-modal-btn"
                          variant="inherit"
                          startIcon={activePerformance.actionIcon}
                          sx={actionButtonSx}
                          onClick={() => handleOpenModal(activePerformance.modalKey)}>
                          {activePerformance.actionLabel}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  selectBuildingInfoContent
                )}
              </Stack>
            }
          />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default NewHomePage;
